import React, { useEffect, useState } from "react";
import DashboardContainer from "../../../components/DashboardContainer";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import renderDataTable from "../../../helper/renderDataTable";
import Breadcrumb from "../../../components/Breadcrumb";

const Reconcilations = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    /* ================= REDUX TOKEN (SAME AS DASHBOARD) ================= */
    const { userLoginToken } = useSelector((state) => state.user);

    /* ================= MONTH ================= */
    const [selectedMonth, setSelectedMonth] = useState("");

    /* ================= API DATA ================= */
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    /* ================= TABLE STATES ================= */
    const [pending, setPending] = useState(false);
    const [resetSearch, setResetSearch] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    /* ================= FETCH DATA ================= */
    useEffect(() => {
        if (!userLoginToken) return;
        fetchReconcilations();
    }, [userLoginToken]);

    const fetchReconcilations = async () => {
        try {
            setPending(true);

            const response = await fetch(
                "https://api.myad360.com/api/v1/FNV89uJ9sud/rest_api/get_reconcilations_data.php",
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        token: userLoginToken, // ✅ dynamic token
                    },
                }
            );

            const result = await response.json();

            if (result?.status === "1") {
                const mappedData = result.data.map((item, index) => ({
                    id: index + 1,
                    month: item.month,
                    payout: Number(item.payout),
                    deduction: Number(item.deductions),
                    net_payment: Number(item.net_payment),
                    status: Number(item.payment_status),
                }));

                setAllData(mappedData);

                if (mappedData.length > 0) {
                    setSelectedMonth(mappedData[0].month);
                } else {
                    // No data at all -> keep pagination in a clean, disabled state
                    setFilteredData([]);
                    setCurrentPage(1);
                    setTotalPages(1);
                }
            } else {
                // API returned no success -> disable pagination cleanly
                setAllData([]);
                setFilteredData([]);
                setCurrentPage(1);
                setTotalPages(1);
            }
        } catch (error) {
            console.error("Reconcilations API Error:", error);
            setAllData([]);
            setFilteredData([]);
            setCurrentPage(1);
            setTotalPages(1);
        } finally {
            setPending(false);
        }
    };

    /* ================= FILTER BY MONTH + PAGINATION RECALC ================= */
    useEffect(() => {
        if (!selectedMonth) {
            setFilteredData([]);
            setCurrentPage(1);
            setTotalPages(1);
            return;
        }

        const filtered = allData.filter(
            (item) => item.month === selectedMonth
        );

        setFilteredData(filtered);

        // Reset to first page whenever the filter (month) or page size changes
        setCurrentPage(1);

        // Recalculate total pages from the actual filtered data.
        // When there's no data, this stays at 1 -> pagination/arrows
        // stay in a disabled state instead of being stuck on stale values.
        const pages = Math.max(1, Math.ceil(filtered.length / pageLimit));
        setTotalPages(pages);
    }, [selectedMonth, allData, pageLimit]);

    /* ================= PAGE CHANGE GUARD ================= */
    // Extra safety: if currentPage ever ends up beyond totalPages
    // (e.g. after deleting rows or a filter change), clamp it back.
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    /* ================= STATUS UI ================= */
    const renderStatus = (status) => {
        return status === 1 ? (
            <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: "#FFF4E5", color: "#D97706" }}
            >
                Success
            </span>
        ) : (
            <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: "#FEF3E5", color: "#92400E" }}
            >
                Pending
            </span>
        );
    };

    /* ================= TABLE COLUMNS ================= */
    const headerStyle = { padding: "12px 16px", color: "#000000", fontWeight: 600 };
    const cellStyle = {
        padding: "12px 16px",
        borderRight: "1px solid #FDE8CD",
        width: "100%",
        whiteSpace: "nowrap",
    };

    const column = [
        {
            name: <div style={headerStyle}>Month</div>,
            selector: (row) => <div style={cellStyle}>{row.month}</div>,
            sortable: true,
            minWidth: "160px",
        },
        {
            name: <div style={headerStyle}>Payout</div>,
            selector: (row) => <div style={cellStyle}>{row.payout.toFixed(2)}</div>,
            sortable: true,
            minWidth: "150px",
        },
        {
            name: <div style={headerStyle}>Deduction</div>,
            selector: (row) => <div style={cellStyle}>{row.deduction.toFixed(2)}</div>,
            sortable: true,
            minWidth: "150px",
        },
        {
            name: <div style={headerStyle}>Net Payment</div>,
            selector: (row) => <div style={cellStyle}>{row.net_payment.toFixed(2)}</div>,
            sortable: true,
            minWidth: "160px",
        },
        {
            name: <div style={headerStyle}>Status</div>,
            selector: (row) => (
                <div style={{ padding: "12px 16px", width: "100%", whiteSpace: "nowrap" }}>
                    {renderStatus(row.status)}
                </div>
            ),
            minWidth: "140px",
        },
    ];

    return (
        <>
            <Breadcrumb pageName="Reconciliation" />

            <DashboardContainer>
                <div className="p-3 sm:p-4 w-full">
                    {/* Select Month - right side, label aligned to box start */}
                    <div className="flex justify-end mb-4">
                        <div className="w-full sm:w-auto">
                            <label className="block text-xs text-[#8A7560] font-medium mb-1 text-left">
                                Select Month
                            </label>
                            <input
                                type="month"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="w-full sm:w-auto border border-[#F0DCC8] px-3 py-2 rounded-lg text-sm text-[#1C1410] focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#FDE8CD] transition-colors [&::-webkit-calendar-picker-indicator]:w-4 [&::-webkit-calendar-picker-indicator]:h-4 sm:[&::-webkit-calendar-picker-indicator]:w-[18px] sm:[&::-webkit-calendar-picker-indicator]:h-[18px]"
                            />
                        </div>
                    </div>

                    {/* Table - no box, no border, no shadow */}
                    <div className="w-full overflow-x-auto">
                        <div style={{ width: "max-content", minWidth: "100%" }}>
                            {renderDataTable(
                                filteredData,
                                column,
                                pending,
                                currentPage,
                                setPageLimit,
                                setCurrentPage,
                                totalPages,
                                pageLimit,
                                setResetSearch,
                                dispatch,
                                navigate
                            )}
                        </div>
                    </div>
                </div>
            </DashboardContainer>
        </>
    );
};

export default Reconcilations;